import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Generic CRUD admin section per contenuti (news, eventi, progetti, faq).
 * Usa una configurazione che definisce campi e endpoint.
 */
export const AdminContent = ({ token, config }) => {
  const { title, endpoint, fields, emptyItem, displayKey, secondaryKey } = config;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null | item object (new or existing)

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}${endpoint}`, {
        headers: { "X-Admin-Password": token },
      });
      if (!res.ok) throw new Error("Errore caricamento");
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error(`Errore caricamento ${title}`);
    }
  }, [endpoint, token, title]);

  useEffect(() => {
    setLoading(true);
    fetchItems().finally(() => setLoading(false));
  }, [fetchItems]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      const isNew = !editing.id;
      const url = isNew ? `${BACKEND}${endpoint}` : `${BACKEND}${endpoint}/${editing.id}`;
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Password": token,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Errore salvataggio");
      }
      toast.success(isNew ? "Creato" : "Aggiornato");
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo elemento?")) return;
    try {
      const res = await fetch(`${BACKEND}${endpoint}/${id}`, {
        method: "DELETE",
        headers: { "X-Admin-Password": token },
      });
      if (!res.ok) throw new Error("Errore eliminazione");
      toast.success("Eliminato");
      fetchItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <section
      data-testid={`admin-content-${title.toLowerCase()}`}
      className="bg-white border border-[#E5E1D8] rounded-xl shadow-sm overflow-hidden"
    >
      <header className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1D8] bg-[#FBF9F6]">
        <h2 className="text-[#1a2620] font-semibold">
          {title} <span className="text-[#5C5C5C] text-sm">({items.length})</span>
        </h2>
        <button
          onClick={() => setEditing({ ...emptyItem })}
          data-testid={`admin-${title.toLowerCase()}-new`}
          className="inline-flex items-center gap-2 bg-[#2E4F3B] hover:bg-[#1f3828] text-white px-3 py-2 rounded-lg text-sm font-semibold transition"
        >
          <Plus className="w-4 h-4" />
          Nuovo
        </button>
      </header>

      {loading ? (
        <div className="p-10 flex items-center justify-center text-[#5C5C5C]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Caricamento…
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-[#8A8A8A] text-sm italic">Nessun elemento. Clicca "Nuovo" per crearne uno.</div>
      ) : (
        <ul className="divide-y divide-[#E5E1D8]">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`admin-${title.toLowerCase()}-row-${it.id}`}
              className="px-5 py-4 flex items-center gap-4 hover:bg-[#FBF9F6]/60"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#1a2620] truncate">{it[displayKey] || "(senza titolo)"}</div>
                {secondaryKey && it[secondaryKey] && (
                  <div className="text-xs text-[#5C5C5C] mt-0.5 truncate">{it[secondaryKey]}</div>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#5C5C5C]">
                <span className="px-2 py-1 rounded bg-[#FBF9F6] border border-[#E5E1D8]">
                  ordine: {it.order ?? 0}
                </span>
                <span className={`px-2 py-1 rounded ${it.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-gray-100 text-gray-700 border border-gray-200"}`}>
                  {it.active ? "attivo" : "nascosto"}
                </span>
              </div>
              <button
                onClick={() => setEditing({ ...it })}
                data-testid={`admin-${title.toLowerCase()}-edit-${it.id}`}
                className="p-2 text-[#2E4F3B] hover:bg-[#2E4F3B]/10 rounded-lg transition"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(it.id)}
                data-testid={`admin-${title.toLowerCase()}-delete-${it.id}`}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={onSave}
            className="bg-white rounded-2xl max-w-2xl w-full my-8 overflow-hidden shadow-2xl"
            data-testid={`admin-${title.toLowerCase()}-modal`}
          >
            <header className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1D8] bg-[#FBF9F6]">
              <h3 className="font-semibold text-[#1a2620]">
                {editing.id ? `Modifica ${title}` : `Nuovo ${title}`}
              </h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1 hover:bg-[#E5E1D8] rounded">
                <X className="w-5 h-5" />
              </button>
            </header>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {fields.map((f) => {
                const value = editing[f.key] ?? "";
                const set = (v) => setEditing((cur) => ({ ...cur, [f.key]: v }));
                if (f.type === "textarea") {
                  return (
                    <div key={f.key}>
                      <label className="block text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-1.5">
                        {f.label} {f.required && "*"}
                      </label>
                      <textarea
                        rows={f.rows || 4}
                        required={f.required}
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        placeholder={f.placeholder}
                        className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2E4F3B] resize-none"
                      />
                    </div>
                  );
                }
                if (f.type === "checkbox") {
                  return (
                    <label key={f.key} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => set(e.target.checked)}
                        className="w-5 h-5 rounded border-2 border-[#E5E1D8] text-[#2E4F3B] focus:ring-[#2E4F3B]"
                      />
                      <span className="text-sm text-[#1a2620] font-medium">{f.label}</span>
                    </label>
                  );
                }
                if (f.type === "select") {
                  return (
                    <div key={f.key}>
                      <label className="block text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-1.5">
                        {f.label}
                      </label>
                      <select
                        value={value}
                        onChange={(e) => set(e.target.value)}
                        className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2E4F3B]"
                      >
                        {f.options.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                }
                return (
                  <div key={f.key}>
                    <label className="block text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-1.5">
                      {f.label} {f.required && "*"}
                    </label>
                    <input
                      type={f.type || "text"}
                      required={f.required}
                      value={value}
                      onChange={(e) => set(f.type === "number" ? Number(e.target.value) : e.target.value)}
                      placeholder={f.placeholder}
                      className="w-full bg-white border-2 border-[#E5E1D8] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2E4F3B]"
                    />
                  </div>
                );
              })}
            </div>

            <footer className="px-6 py-4 border-t border-[#E5E1D8] bg-[#FBF9F6] flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm font-semibold text-[#1a2620] hover:bg-[#E5E1D8] rounded-lg"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#2E4F3B] hover:bg-[#1f3828] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salva
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
};

// Configurazioni per ogni tipo di contenuto
export const NEWS_CONFIG = {
  title: "News",
  endpoint: "/api/admin/news",
  displayKey: "title",
  secondaryKey: "excerpt",
  emptyItem: { title: "", excerpt: "", content: "", image_url: "", image_fit: "cover", author: "Per Asciano", order: 0, active: true, cta_label: "", cta_url: "" },
  fields: [
    { key: "title", label: "Titolo", required: true },
    { key: "excerpt", label: "Estratto (breve)", type: "textarea", rows: 2, required: true },
    { key: "content", label: "Contenuto completo", type: "textarea", rows: 8, required: true },
    { key: "image_url", label: "URL immagine", placeholder: "/logo-fai.png oppure https://..." },
    { key: "image_fit", label: "Adattamento immagine", type: "select", options: [
      { value: "cover", label: "Cover (riempi e taglia)" },
      { value: "contain", label: "Contain (mostra tutto, ideale per loghi)" },
    ]},
    { key: "cta_label", label: "Etichetta bottone CTA (opzionale)", placeholder: "es. Vota Monte Faeta sul FAI" },
    { key: "cta_url", label: "Link CTA (opzionale)", placeholder: "https://... oppure /monte-faeta" },
    { key: "author", label: "Autore" },
    { key: "order", label: "Ordine", type: "number" },
    { key: "active", label: "Pubblicata (visibile sul sito)", type: "checkbox" },
  ],
};

export const EVENTS_CONFIG = {
  title: "Eventi",
  endpoint: "/api/admin/events",
  displayKey: "title",
  secondaryKey: "date_label",
  emptyItem: { title: "", date_label: "", time_label: "", location: "", description: "", image_url: "", link: "", is_past: false, order: 0, active: true },
  fields: [
    { key: "title", label: "Titolo evento", required: true },
    { key: "date_label", label: "Data (testo libero)", placeholder: "es. Venerdì 19 giugno 2026", required: true },
    { key: "time_label", label: "Orario", placeholder: "es. 19:30 - 20:00" },
    { key: "location", label: "Luogo", required: true },
    { key: "description", label: "Descrizione", type: "textarea", rows: 5, required: true },
    { key: "image_url", label: "URL immagine" },
    { key: "link", label: "Link evento", placeholder: "/riunione o https://..." },
    { key: "is_past", label: "Evento già concluso", type: "checkbox" },
    { key: "order", label: "Ordine", type: "number" },
    { key: "active", label: "Visibile sul sito", type: "checkbox" },
  ],
};

export const PROJECTS_CONFIG = {
  title: "Progetti",
  endpoint: "/api/admin/projects",
  displayKey: "title",
  secondaryKey: "summary",
  emptyItem: { title: "", summary: "", content: "", image_url: "", image_fit: "cover", status: "in_corso", cta_label: "", cta_url: "", order: 0, active: true },
  fields: [
    { key: "title", label: "Titolo progetto", required: true },
    { key: "summary", label: "Sommario breve", type: "textarea", rows: 2, required: true },
    { key: "content", label: "Descrizione completa", type: "textarea", rows: 8, required: true },
    { key: "image_url", label: "URL immagine", placeholder: "/logo-fai.png oppure https://..." },
    { key: "image_fit", label: "Adattamento immagine", type: "select", options: [
      { value: "cover", label: "Cover (riempi e taglia)" },
      { value: "contain", label: "Contain (ideale per loghi)" },
    ]},
    { key: "cta_label", label: "Etichetta bottone CTA (opzionale)", placeholder: "es. Richiedi aiuto pulizia" },
    { key: "cta_url", label: "Link CTA (opzionale)", placeholder: "/aiuto-pulizia oppure https://..." },
    { key: "status", label: "Stato", type: "select", options: [
      { value: "in_corso", label: "In corso" },
      { value: "completato", label: "Completato" },
      { value: "futuro", label: "In arrivo / Futuro" },
    ]},
    { key: "order", label: "Ordine", type: "number" },
    { key: "active", label: "Visibile sul sito", type: "checkbox" },
  ],
};

export const PRESS_CONFIG = {
  title: "Sulla stampa",
  endpoint: "/api/admin/press",
  displayKey: "title",
  secondaryKey: "source",
  emptyItem: { title: "", source: "", date_label: "", author: "", excerpt: "", image_url: "", kind: "article", url: "", clipping_url: "", order: 0, active: true },
  fields: [
    { key: "title", label: "Titolo articolo", required: true },
    { key: "source", label: "Testata (giornale/media)", placeholder: "es. La Nazione", required: true },
    { key: "date_label", label: "Data (testo libero)", placeholder: "es. 19 luglio 2026" },
    { key: "author", label: "Autore / firma", placeholder: "es. Mario Rossi" },
    { key: "excerpt", label: "Estratto / sommario", type: "textarea", rows: 3 },
    { key: "kind", label: "Tipo", type: "select", options: [
      { value: "article", label: "Articolo online (apre il link)" },
      { value: "video", label: "Video (apre il link)" },
      { value: "clipping", label: "Ritaglio cartaceo (apre l'immagine a schermo intero)" },
    ]},
    { key: "image_url", label: "URL immagine di anteprima", placeholder: "https://..." },
    { key: "url", label: "Link esterno (per articolo/video)", placeholder: "https://..." },
    { key: "clipping_url", label: "URL immagine ritaglio a schermo intero (solo cartaceo)", placeholder: "https://..." },
    { key: "order", label: "Ordine", type: "number" },
    { key: "active", label: "Visibile sul sito", type: "checkbox" },
  ],
};

export const PRODUCTS_CONFIG = {
  title: "Prodotti shop",
  endpoint: "/api/admin/shop-products",
  displayKey: "name",
  secondaryKey: "description",
  emptyItem: { name: "", price: 0, sizes: "", image_url: "", description: "", order: 0, active: true },
  fields: [
    { key: "name", label: "Nome prodotto", required: true },
    { key: "price", label: "Prezzo (€)", type: "number", required: true },
    { key: "sizes", label: "Taglie (separate da virgola, vuoto = taglia unica)", placeholder: "S,M,L,XL" },
    { key: "image_url", label: "URL immagine", placeholder: "https://... oppure /maglietta-per-asciano.png" },
    { key: "description", label: "Descrizione", type: "textarea", rows: 3 },
    { key: "order", label: "Ordine", type: "number" },
    { key: "active", label: "In vendita (visibile nello shop)", type: "checkbox" },
  ],
};

export const FAQ_CONFIG = {
  title: "FAQ",
  endpoint: "/api/admin/faq",
  displayKey: "question",
  secondaryKey: "answer",
  emptyItem: { question: "", answer: "", order: 0, active: true },
  fields: [
    { key: "question", label: "Domanda", required: true },
    { key: "answer", label: "Risposta", type: "textarea", rows: 6, required: true },
    { key: "order", label: "Ordine", type: "number" },
    { key: "active", label: "Visibile sul sito", type: "checkbox" },
  ],
};

export const NOTICES_CONFIG = {
  title: "Avvisi (Centro Operativo)",
  endpoint: "/api/admin/notices",
  displayKey: "title",
  secondaryKey: "message",
  emptyItem: { title: "", message: "", level: "info", cta_label: "", cta_url: "", order: 0, active: true },
  fields: [
    { key: "title", label: "Titolo avviso", required: true },
    { key: "message", label: "Messaggio", type: "textarea", rows: 4, required: true },
    { key: "level", label: "Tipo / livello", type: "select", options: [
      { value: "alert", label: "🔴 Allerta (rossa) — es. rischio incendi" },
      { value: "warning", label: "🟡 Attenzione (gialla) — es. chiusura sentieri" },
      { value: "info", label: "⚪ Comunicazione (bianca) — es. riunione pubblica" },
      { value: "success", label: "🟢 Novità (verde) — es. giornata volontariato" },
    ]},
    { key: "cta_label", label: "Etichetta bottone (opzionale)", placeholder: "es. Scopri di più" },
    { key: "cta_url", label: "Link bottone (opzionale)", placeholder: "/eventi oppure https://..." },
    { key: "order", label: "Ordine", type: "number" },
    { key: "active", label: "Visibile sul sito", type: "checkbox" },
  ],
};
